import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, miAsistenciaService } from '../services/api';
import Pagination from '../components/Pagination';
import ConfirmPopup from '../components/ConfirmPopup';
import { formatDuracionClase } from '../utils/duration';
import './MiAsistencia.css';

const CURSOS_POR_PAGINA = 10;

const formatDateFull = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

/** Devuelve true si ahora (local) está dentro del horario de clase para esa fecha. */
const dentroDelHorario = (curso, fechaStr) => {
  if (!curso.hora_inicio_clase || curso.tomar_asistencia_durante !== 'horario') return false;
  const horaStr = (curso.hora_inicio_clase + '').trim().substring(0, 5);
  if (!/^\d{2}:\d{2}$/.test(horaStr)) return false;
  const duracion = Number(curso.duracion_clase) || 0;
  const start = new Date(fechaStr + 'T' + horaStr + ':00');
  const end = new Date(start.getTime() + duracion * 60 * 60 * 1000);
  const now = Date.now();
  return now >= start.getTime() && now <= end.getTime();
};

const MiAsistencia = () => {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [marcando, setMarcando] = useState(null); // { cursoId, fecha }
  const [errorMarcar, setErrorMarcar] = useState('');
  const [tabActiva, setTabActiva] = useState('activos'); // 'activos' | 'terminados'
  const [selectedCursoId, setSelectedCursoId] = useState(null);
  const [paginaCursos, setPaginaCursos] = useState(1);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadCursos();
  }, [navigate]);

  const loadCursos = async () => {
    try {
      setLoading(true);
      setError('');
      setErrorMarcar('');
      const data = await miAsistenciaService.getCursos();
      setCursos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar tus cursos.');
      setCursos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarcar = async (cursoId, fecha) => {
    setErrorMarcar('');
    setMarcando({ cursoId, fecha });
    try {
      await miAsistenciaService.marcar(cursoId, fecha);
      await loadCursos();
    } catch (err) {
      setErrorMarcar(err.response?.data?.error || 'No se pudo registrar la asistencia.');
    } finally {
      setMarcando(null);
    }
  };

  /** Fecha de hoy en zona local (ej. Argentina) como YYYY-MM-DD. */
  const hoyStr = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const esHoy = (fechaStr) => fechaStr === hoyStr();
  const esFutura = (fechaStr) => fechaStr > hoyStr();

  /** Solo habilitar el botón durante el día (si curso es "dia") o durante el horario de clase (si es "horario"). */
  /** Si asistencia_manual está activo, solo habilitar si el día está en dias_manuales_habilitados (puede ser cualquier fecha, no solo hoy). */
  const puedeMarcarAhora = (curso, fechaStr) => {
    if (curso.asistencias && curso.asistencias[fechaStr] === true) return false;

    if (curso.asistencia_manual) {
      const diasHabilitados = curso.dias_manuales_habilitados || [];
      return diasHabilitados.includes(fechaStr);
    }

    if (!esHoy(fechaStr)) return false;
    if (curso.tomar_asistencia_durante === 'dia') return true;
    if (curso.tomar_asistencia_durante === 'horario') return dentroDelHorario(curso, fechaStr);
    return false;
  };

  const hoy = hoyStr();
  const cursosActivos = cursos.filter((c) => {
    if (c.activo === false) return false;
    if (!c.fecha_fin) return true;
    return c.fecha_fin >= hoy;
  });
  const cursosTerminados = cursos.filter((c) => {
    if (c.activo === false) return true;
    if (!c.fecha_fin) return false;
    return c.fecha_fin < hoy;
  });

  const listaParaTab = tabActiva === 'activos' ? cursosActivos : cursosTerminados;
  const listaIdsKey = useMemo(() => listaParaTab.map((c) => c.id).join(','), [listaParaTab]);
  const totalPaginas = Math.max(1, Math.ceil(listaParaTab.length / CURSOS_POR_PAGINA));
  const listaPaginada = useMemo(() => {
    const inicio = (paginaCursos - 1) * CURSOS_POR_PAGINA;
    return listaParaTab.slice(inicio, inicio + CURSOS_POR_PAGINA);
  }, [listaParaTab, paginaCursos]);
  const cursoSeleccionado = listaParaTab.find((c) => c.id === selectedCursoId) || null;
  const rangoDesde = listaParaTab.length === 0 ? 0 : (paginaCursos - 1) * CURSOS_POR_PAGINA + 1;
  const rangoHasta = Math.min(paginaCursos * CURSOS_POR_PAGINA, listaParaTab.length);

  useEffect(() => {
    setPaginaCursos(1);
  }, [tabActiva]);

  useEffect(() => {
    if (paginaCursos > totalPaginas) {
      setPaginaCursos(totalPaginas);
    }
  }, [paginaCursos, totalPaginas]);

  useEffect(() => {
    if (!listaIdsKey) {
      setSelectedCursoId(null);
      return;
    }
    setSelectedCursoId((prev) => {
      const ids = listaIdsKey.split(',').map((id) => parseInt(id, 10));
      if (prev && ids.includes(prev)) return prev;
      return ids[0];
    });
  }, [tabActiva, listaIdsKey]);

  useEffect(() => {
    if (!selectedCursoId || !listaIdsKey) return;
    const ids = listaIdsKey.split(',').map((id) => parseInt(id, 10));
    const indice = ids.indexOf(selectedCursoId);
    if (indice < 0) return;
    const paginaDelCurso = Math.floor(indice / CURSOS_POR_PAGINA) + 1;
    setPaginaCursos((prev) => (prev === paginaDelCurso ? prev : paginaDelCurso));
  }, [selectedCursoId, listaIdsKey]);

  const handlePaginaCursos = useCallback((page) => {
    setPaginaCursos(page);
    const inicio = (page - 1) * CURSOS_POR_PAGINA;
    const primero = listaParaTab[inicio];
    if (primero) setSelectedCursoId(primero.id);
  }, [listaParaTab]);

  const renderCursoDetalle = (curso) => (
  <>
    <div className="mi-asistencia-panel-header">
      <h2 className="mi-asistencia-curso-nombre">{curso.nombre}</h2>
    </div>
    {curso.asistencia_manual && (
      <p className="mi-asistencia-curso-horario">
        Asistencia manual: solo los días habilitados en la planilla permiten marcar asistencia.
      </p>
    )}
    {!curso.asistencia_manual && curso.tomar_asistencia_durante === 'horario' && curso.hora_inicio_clase && (
      <p className="mi-asistencia-curso-horario">
        Horario: {curso.hora_inicio_clase.substring(0, 5)}
        {(() => {
          const d = formatDuracionClase(curso.duracion_clase);
          return d ? ` (${d})` : '';
        })()} — Asistencia solo durante el horario.
      </p>
    )}
    {!curso.asistencia_manual && curso.tomar_asistencia_durante === 'dia' && (
      <p className="mi-asistencia-curso-horario">Asistencia durante todo el día de la clase.</p>
    )}
    <div className="mi-asistencia-fechas">
      {curso.fechas_clase && curso.fechas_clase.length === 0 && (
        <p className="mi-asistencia-sin-fechas">No hay fechas de clase en el rango mostrado.</p>
      )}
      {curso.fechas_clase &&
        curso.fechas_clase.map((fechaStr) => {
          const yaMarcado = curso.asistencias && curso.asistencias[fechaStr] === true;
          const estaMarcando = marcando && marcando.cursoId === curso.id && marcando.fecha === fechaStr;
          const puedeMarcar = puedeMarcarAhora(curso, fechaStr);
          const esHoyFecha = esHoy(fechaStr);
          const esFuturaFecha = esFutura(fechaStr);
          const esPasadaFecha = fechaStr < hoyStr();
          const mostrarBotonDeshabilitado =
            esHoyFecha && !yaMarcado && !puedeMarcar && (
              curso.tomar_asistencia_durante === 'horario' ||
              (curso.asistencia_manual && !curso.dias_manuales_habilitados?.includes(fechaStr))
            );

          return (
            <div
              key={fechaStr}
              className={`mi-asistencia-fila ${esHoyFecha ? 'mi-asistencia-fila-hoy' : ''}`}
            >
              <span className="mi-asistencia-fecha">{formatDateFull(fechaStr)}</span>
              <span className="mi-asistencia-estado">
                {yaMarcado && <span className="mi-asistencia-presente">✓ Presente</span>}
                {!yaMarcado && puedeMarcar && (
                  <button
                    type="button"
                    className="mi-asistencia-btn-marcar"
                    onClick={() => handleMarcar(curso.id, fechaStr)}
                    disabled={estaMarcando}
                  >
                    {estaMarcando ? 'Marcando...' : 'Dar presente'}
                  </button>
                )}
                {mostrarBotonDeshabilitado && (
                  <button
                    type="button"
                    className="mi-asistencia-btn-marcar mi-asistencia-btn-deshabilitado"
                    disabled
                    title={
                      curso.asistencia_manual && !curso.dias_manuales_habilitados?.includes(fechaStr)
                        ? 'Este día no está habilitado para marcar asistencia'
                        : 'Solo durante el horario de la clase'
                    }
                  >
                    Dar presente
                  </button>
                )}
                {!yaMarcado && !puedeMarcar && esFuturaFecha && (
                  <span className="mi-asistencia-proxima">Próxima clase</span>
                )}
                {!yaMarcado && !puedeMarcar && esPasadaFecha && (
                  <span className="mi-asistencia-pasada">Pasada</span>
                )}
              </span>
            </div>
          );
        })}
    </div>
  </>
  );

  if (loading) {
    return (
      <div className="mi-asistencia-container">
        <div className="mi-asistencia-loading">
          <div className="mi-asistencia-spinner" />
          <p>Cargando tus cursos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mi-asistencia-container">
      <div className="mi-asistencia-header">
        <h1>Mi Asistencia</h1>
        <p className="mi-asistencia-subtitle">
          Marca tu asistencia durante el horario de la clase.
        </p>
      </div>

      <ConfirmPopup
        open={Boolean(error || errorMarcar)}
        title="Error"
        message={errorMarcar || error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => { setError(''); setErrorMarcar(''); }}
        onCancel={() => { setError(''); setErrorMarcar(''); }}
        variant="danger"
      />

      <div className="mi-asistencia-tabs">
        <button
          type="button"
          className={`mi-asistencia-tab ${tabActiva === 'activos' ? 'mi-asistencia-tab-activa' : ''}`}
          onClick={() => setTabActiva('activos')}
        >
          Cursos activos {cursosActivos.length > 0 && `(${cursosActivos.length})`}
        </button>
        <button
          type="button"
          className={`mi-asistencia-tab ${tabActiva === 'terminados' ? 'mi-asistencia-tab-activa' : ''}`}
          onClick={() => setTabActiva('terminados')}
        >
          Cursos terminados / inactivos {cursosTerminados.length > 0 && `(${cursosTerminados.length})`}
        </button>
      </div>

      {listaParaTab.length === 0 && !error ? (
        <div className="mi-asistencia-empty">
          <p>
            {tabActiva === 'activos'
              ? 'No tenés cursos activos o no hay fechas de clase en el rango mostrado.'
              : 'No tenés cursos terminados o inactivos.'}
          </p>
        </div>
      ) : (
        <div className="mi-asistencia-layout-wrap">
          <div className="mi-asistencia-layout">
            <div className="mi-asistencia-nav-col">
              <nav className="mi-asistencia-nav" aria-label="Cursos">
                {listaPaginada.map((curso) => {
                  const activo = selectedCursoId === curso.id;
                  return (
                    <button
                      key={curso.id}
                      type="button"
                      className={`mi-asistencia-nav-btn ${activo ? 'mi-asistencia-nav-btn-activa' : ''}`}
                      onClick={() => setSelectedCursoId(curso.id)}
                      aria-current={activo ? 'true' : undefined}
                    >
                      <span className="mi-asistencia-nav-btn-text">{curso.nombre}</span>
                    </button>
                  );
                })}
              </nav>
              {listaParaTab.length > CURSOS_POR_PAGINA && (
                <p className="mi-asistencia-pagination-info">
                  Mostrando {rangoDesde}–{rangoHasta} de {listaParaTab.length} cursos
                </p>
              )}
            </div>
            <section className="mi-asistencia-panel" aria-live="polite">
              {cursoSeleccionado ? (
                renderCursoDetalle(cursoSeleccionado)
              ) : (
                <p className="mi-asistencia-sin-seleccion">Seleccioná un curso para ver las fechas de asistencia.</p>
              )}
            </section>
          </div>
          {listaParaTab.length > CURSOS_POR_PAGINA && (
            <Pagination
              currentPage={paginaCursos}
              totalPages={totalPaginas}
              onPageChange={handlePaginaCursos}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MiAsistencia;
